import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyDeviceStatus, getMyRegisterableBranches } from "../../utils/Misc";
import { getOrCreateDeviceId } from "../../utils/DeviceId";
import RegisterDeviceForm from "./RegisterDeviceForm";

// Device-registration rollout: force branch SPOCs specifically to register
// the device they're using, since they're the ones who can actually do it
// (see device.controller.js's canRegisterForBranch). Mounted once in
// ProtectedRoute so it covers every authenticated page, not just the
// attendance dashboard - a SPOC can't get around it by navigating
// elsewhere.
//
// Deliberately shows a self-register form only for SPOCs - a regular
// employee has no ability to register a device themselves, so blocking
// their whole session the same way would serve no purpose. For them, an
// unregistered device just hides the Clock-In button with an inline
// message instead (see AttendanceDetails.jsx / AttendenceEmployee.jsx),
// since Phase 2 removed GPS as a fallback for everyone (see
// attendance.service.js's insertEventLog).
const SpocDeviceRegistrationGate = () => {
  const [branches, setBranches] = useState(null); // null = not checked yet
  const [registered, setRegistered] = useState(true); // fail open until checked

  const checkStatus = async () => {
    try {
      const myBranches = await getMyRegisterableBranches();
      setBranches(myBranches || []);
      if (!myBranches || myBranches.length === 0) {
        setRegistered(true); // not a SPOC - nothing to gate
        return;
      }
      const status = await getMyDeviceStatus(getOrCreateDeviceId());
      setRegistered(Boolean(status?.registered));
    } catch (err) {
      // Fail open - a status-check hiccup shouldn't lock a SPOC out of the
      // entire app.
      setRegistered(true);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const isSpoc = branches && branches.length > 0;
  if (!isSpoc || registered) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "420px",
          width: "100%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        }}
      >
        <h5 className="mb-2">Register This Device</h5>
        <p className="text-muted mb-3">
          {branches.length === 1 ? (
            <>
              You're the facility SPOC for <strong>{branches[0].branch_name}</strong>, which
              doesn't have a registered clock-in device yet. Register the device you're using
              now so employees there can clock in from it without relying on GPS.
            </>
          ) : (
            <>
              You're the facility SPOC for multiple branches without a registered clock-in
              device: <strong>{branches.map((b) => b.branch_name).join(", ")}</strong>. Pick
              which one this device belongs to below, so employees there can clock in from it
              without relying on GPS.
            </>
          )}
        </p>
        <RegisterDeviceForm branches={branches} onRegistered={checkStatus} />
        <Link to="/logout" className="btn btn-outline-secondary btn-sm mt-3">
          Logout
        </Link>
      </div>
    </div>
  );
};

export default SpocDeviceRegistrationGate;
